import React, { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

export type WistiaPlayerCustomElement = HTMLElement & {
  pause: () => Promise<void>;
  mediaId?: string;
  autoplay?: boolean;
  volume?: number;
  isPaused?: boolean;
  aspect?: number;
  controlsVisibleOnLoad?: boolean;
  __handleApiReady?: (event: Event) => void;
};

export type VideoPlayerProps = {
  mediaId: string;
  autoplay?: boolean;
  poster?: string;
  playerColor?: string;
  controlsVisibleOnLoad?: boolean;
  volume?: number;
  isPaused?: boolean;
  onPlayerInit?: (element: WistiaPlayerCustomElement) => void;
};

function VideoPlayer({
  mediaId,
  autoplay,
  poster,
  playerColor,
  controlsVisibleOnLoad,
  volume,
  isPaused,
  onPlayerInit,
}: Readonly<VideoPlayerProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerElementRef = useRef<WistiaPlayerCustomElement | null>(null);
  const [isElementDefined, setIsElementDefined] = useState(false);

  useEffect(() => {
    if (window.customElements.get('wistia-player')) {
      if (!isElementDefined) setIsElementDefined(true);
      return;
    }

    let isMounted = true;

    window.customElements
      .whenDefined('wistia-player')
      .then(() => {
        if (isMounted) setIsElementDefined(true);
      })
      .catch((error) => {
        console.error('VideoPlayer: Error waiting for <wistia-player> definition:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [isElementDefined]);

  useEffect(() => {
    if (!containerRef.current || !isElementDefined || !mediaId) return;

    const container = containerRef.current;

    const newPlayerElement = document.createElement('wistia-player') as WistiaPlayerCustomElement;
    playerElementRef.current = newPlayerElement;

    newPlayerElement.setAttribute('media-id', mediaId);
    newPlayerElement.style.height = '100%';

    if (poster !== undefined) {
      newPlayerElement.setAttribute('still-url', poster);
    }
    if (playerColor !== undefined) {
      newPlayerElement.setAttribute('player-color', playerColor);
    }
    if (controlsVisibleOnLoad) {
      newPlayerElement.setAttribute('controls-visible-on-load', '');
    }
    if (autoplay !== undefined) {
      newPlayerElement.autoplay = autoplay;
    }
    if (volume !== undefined) {
      newPlayerElement.volume = volume;
    }

    const handleApiReady = () => {
      const player = playerElementRef.current;
      if (player && volume !== undefined && typeof player.volume === 'number') {
        player.volume = volume;
        player.classList.add('ready');
      }
      if (onPlayerInit) {
        onPlayerInit(newPlayerElement);
      }
    };

    newPlayerElement.__handleApiReady = handleApiReady;
    newPlayerElement.addEventListener('api-ready', handleApiReady);

    container.appendChild(newPlayerElement);

    return () => {
      if (newPlayerElement.__handleApiReady) {
        newPlayerElement.removeEventListener('api-ready', newPlayerElement.__handleApiReady);
      }
      if (newPlayerElement.parentNode === container) {
        container.removeChild(newPlayerElement);
      }
      if (playerElementRef.current === newPlayerElement) {
        playerElementRef.current = null;
      }
    };
  }, [
    mediaId,
    isElementDefined,
    autoplay,
    poster,
    playerColor,
    controlsVisibleOnLoad,
    volume,
    isPaused,
    onPlayerInit,
  ]);

  return (
    <>
      <Script
        id="wistia-player-script"
        src="https://fast.wistia.com/player.js"
        strategy="afterInteractive"
      />
      <div className="video-player" style={{ height: '100%' }} ref={containerRef}></div>
    </>
  );
}

export default VideoPlayer;
